









import {Component, Prop, Vue, Watch} from 'vue-property-decorator';
import {ActiveInject, ActiveProvide} from "@/support/reactive"
import JdhSizeDetector from "@/design-system/shared/JdhSizeDetector.vue"

/**
 * @status stable
 * @published 4.0.0
 * @changelog 4.0.0 Published.
 * @changelog 4.2.0 Added support for stacked contexts.
 * @changelog 4.3.0 Added support for columns and auto-stack.
 */
@Component({
    components: {JdhSizeDetector}
})
export default class JdsBreakdown extends Vue {
    @ActiveInject({from: 'jds.breakdown.card', default: true}) cardContext!: boolean
    @ActiveInject({from: 'jds.breakdown.stack', default: false}) stackContext!: boolean

    @ActiveProvide('jds.breakdown.stack-items') childrenShouldStack = false;

    autoColumns = 1
    autoStack = false   // go into stack when too narrow
    overflow = false    // go into stack when overflowing detected

    /**
     * Stack this breakdown vertically instead of horizontally. This is generally inferred automatically from where
     * the breakdown is being used, so only set this manually when necessary.
     *
     * Engaged automatically when `columns` is set, or there isn't enough space not to stack.
     *
     * @avoid
     */
    @Prop({type: Boolean, default: false}) stack!: boolean

    /**
     * Numeric string. **Sets stack mode**, and defines how many columns to show. Try to comfortably fit the information
     * you plan to show, so it fits in one column without wrapping. A maximum of 4 is reasonable.
     *
     * Automatically adjusts to fewer columns if the target number isn't possible.
     *
     * @default 1
     */
    @Prop() columns!: string

    @Watch('shouldStack', {immediate: true})
    updateChildrenContext() {
        this.childrenShouldStack = this.shouldStack
    }

    get shouldStack() {
        return this.stack || this.stackContext || !!this.columns || this.overflow || this.autoStack
    }

    get classes() {
        return {
            'jdsc-breakdown-stack': this.shouldStack,
            'jdsc-breakdown-card': this.cardContext
        }
    }

    get numberOfColumns() {
        if (this.columns) {
            if (parseInt(this.columns) < 1 || Number.isNaN(parseInt(this.columns))) return 1;
            // if we think there isn't enough room ...
            if (this.autoColumns < parseInt(this.columns)) {
                // ... reduce the columns down to an even factor of the desired columns
                for (let i = this.autoColumns; i > 1; i--) {
                    if (parseInt(this.columns) % i === 0) return i
                }
                return 1
            } else {
                return this.columns
            }
        }
        // default is still one column.
        return 1;
    }

    get style() {
        if (this.shouldStack) {
            return {
                "grid-template-columns": `[left] repeat(${this.numberOfColumns}, 1fr) [center right]`
            }
        }
        return {}
    }

    onResize({ width }: {width: number}) {
        this.autoColumns = Math.max(1, Math.floor(width / 200))
        this.autoStack = width < 400
    }
}
